import { useEffect, useRef, useState } from "react";
import { IFood } from "../models/IFood";
import { S3_IMAGE_BASE_URL } from "../services/config";
import MobileFoodDetailPopup from "./MobileFoodDetailPopup";
import { fetchVerificationStatus, isProfileComplete } from "../utils/verificationCache";
import { isLoggedIn } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

interface Props {
  foods: IFood[];
  restaurantName: string;
  minAmount: number;
  orderDisabled: boolean;
  onClose: () => void;
}

function MobileMenuSearchPopup({ foods, restaurantName, minAmount, orderDisabled, onClose }: Props) {
  useBodyScrollLock();
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [selectedFood, setSelectedFood] = useState<IFood | null>(null);
  const [closing, setClosing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 350);
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
  };

  const filtered = query.trim().length < 1
    ? []
    : foods.filter((f) => {
        const name = (lang === "en" ? f.foodNameEn : f.foodNameTr).toLowerCase();
        const contents = (f.foodContents ?? "").toLowerCase();
        const q = query.trim().toLowerCase();
        return name.includes(q) || contents.includes(q);
      });

  const handleSelectFood = async (food: IFood) => {
    if (orderDisabled) return;
    if (isLoggedIn()) {
      await fetchVerificationStatus();
      if (!isProfileComplete()) return;
    }
    setSelectedFood(food);
  };

  return (
    <>
      <div className={`mobile-bottom-popup-overlay${closing ? " mobile-bottom-popup-overlay--closing" : ""}`}>
        <div className={`mobile-bottom-popup ${closing ? "mobile-bottom-popup--closing" : "mobile-bottom-popup--animate"}`} style={{ height: "100vh", maxHeight: "100vh" }}>
          {/* Search header */}
          <div className="d-flex align-items-center gap-2 px-3 pt-3 pb-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
            <button
              className="btn p-0 border-0 bg-transparent"
              onClick={handleClose}
              style={{ fontSize: "1.2rem", color: "#333", lineHeight: 1 }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <div className="flex-grow-1 position-relative">
              <input
                ref={inputRef}
                type="text"
                className="form-control rounded-pill ps-4"
                style={{ fontSize: "0.85rem", background: "#f5f5f5", border: "none" }}
                placeholder={`${restaurantName} ${t("search.inRestaurant")}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  className="btn position-absolute border-0 p-0"
                  style={{ right: 12, top: "50%", transform: "translateY(-50%)", color: "#999" }}
                  onClick={() => setQuery("")}
                >
                  <i className="bi bi-x-circle-fill"></i>
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {query.trim().length > 0 && filtered.length === 0 && (
              <div className="text-center text-muted py-5">
                <i className="bi bi-search" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2 small">{t("search.noResults")}</p>
              </div>
            )}

            {filtered.map((food) => {
              const name = lang === "en" ? food.foodNameEn : food.foodNameTr;
              return (
                <div
                  key={food.id}
                  className="d-flex align-items-center px-3 py-3"
                  style={{ borderBottom: "1px solid #f5f5f5", cursor: "pointer" }}
                  onClick={() => handleSelectFood(food)}
                >
                  <div className="flex-grow-1 pe-3">
                    <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>{name}</div>
                    {food.foodContents && (
                      <div className="text-muted small mt-1" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {food.foodContents}
                      </div>
                    )}
                    {food.hasDiscount && food.discountedPriceText ? (
                      <div className="fw-bold mt-1 d-flex align-items-center gap-1" style={{ fontSize: "0.85rem" }}>
                        <s className="text-muted fw-normal" style={{ fontSize: "0.72rem" }}>
                          {food.foodPriceText}
                        </s>
                        <span style={{ color: "#c20e1a" }}>
                          {food.discountedPriceText} {food.currency.currencyDisplayText}
                        </span>
                      </div>
                    ) : (
                      <div className="fw-bold mt-1" style={{ color: "#c20e1a", fontSize: "0.85rem" }}>
                        {food.foodPriceText} {food.currency.currencyDisplayText}
                      </div>
                    )}
                  </div>
                  {food.foodImagePath && (
                    <img
                      src={S3_IMAGE_BASE_URL + food.foodImagePath}
                      alt={name}
                      style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 12, flexShrink: 0 }}
                    />
                  )}
                </div>
              );
            })}

            {query.trim().length === 0 && (
              <div className="text-center text-muted py-5">
                <i className="bi bi-search" style={{ fontSize: "2rem", color: "#ddd" }}></i>
                <p className="mt-2 small">{t("search.startTyping")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedFood && (
        <MobileFoodDetailPopup
          food={selectedFood}
          minAmount={minAmount}
          onClose={() => setSelectedFood(null)}
        />
      )}
    </>
  );
}

export default MobileMenuSearchPopup;
